import {
  CHECKOUT_SESSION_LIFETIME_MS,
  LAUNCH_BLOCKER_CODES,
  type Quote,
  QUOTE_REFUSAL_MESSAGES,
  QUOTE_REFUSAL_REASONS,
  type QuoteRefusalReason,
  type QuoteView,
  type StudyStepSlug,
  displayMinutes,
  isQuoteLive,
} from '@sigora/domain';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { z } from 'zod';

import {
  type CheckoutCredentials,
  type Study,
  type StudyPayment,
  cancelCheckout,
  createPurchase,
  issueQuote,
  openCheckout,
  refreshPurchase,
  retrySetup,
} from '../api/client.ts';
import { domainCodeOf, isApiError } from '../api/errors.ts';
import { OPEN_BRIEF_LABEL } from '../studies/brief-copy.ts';
import { dateOf } from '../studies/dates.ts';
import { LaunchBlockers, useLaunchCheck } from './LaunchBlockers.tsx';
import { LaunchSummary, useLaunchSummaryReads } from './LaunchSummary.tsx';
import {
  CHECKOUT_CANCELLED,
  closedCheckoutLine,
  FIELDING_TIME,
  LAUNCHED_UNDATED,
  launchedSummaryLine,
  OPEN_AUDIENCE_STEP,
  PAID_BEFORE_CANCEL,
  PAY_LABEL,
  PAYING_LABEL,
  PAYMENT_TAKEN,
  PRICE_RECHECKING,
  PRICE_UPDATING,
  mostPeopleLine,
  PRICE_ALL_IN,
  PRICE_FAILED,
  PRICE_LOADING,
  PRICE_RETRY,
  priceAcceptedLine,
  priceChangedLine,
  priceHoldsUntil,
  pricedMinutesLine,
  SETUP_RESTARTED,
} from './launch-copy.ts';
import {
  type CheckoutRefusal,
  LaunchPayment,
  type PaymentAction,
  PaymentProblems,
} from './LaunchPayment.tsx';
import { priceLabel, priceTimeLabel } from './launch-format.ts';
import { isLaunchedState } from './state.ts';
import { useBoundedPoll } from './use-bounded-poll.ts';

/**
 * What the payment form is given beside the credentials, to tell Launch what happened in it and to
 * open it for a checkout this panel holds no credentials for.
 */
export interface CheckoutFormActions {
  /** The form says the payment is complete: Launch asks the server, which reads the provider. */
  onComplete: () => void;
  /** The server answered that the checkout is no longer open, with the status it is in now. */
  onClosed: (status: StudyPayment) => void;
  /** The fake's checkout was expired from its test box. */
  onExpired: () => void;
  /** The server refused the checkout for good when the form asked for it, in its own words. */
  onRefused: (refusal: CheckoutRefusal) => void;
  /** Asks for the pending purchase's checkout, whose answer brings the credentials. */
  onContinue: () => void;
  /** Whether that ask is out. */
  continuing: boolean;
  /** Whether the provider is still being asked about the open checkout before Continue is offered. */
  checking: boolean;
  /** Whether any payment request is out; every control waits for it. */
  busy: boolean;
}

export interface LaunchPanelProps {
  studyId: string;
  study: Study | null;
  /** Bumped by the shell when a turn ends, so the price is re-read after the study changes. */
  refreshToken: number;
  /** Asks the shell to re-read the study, which is how the panel learns the launch landed. */
  onStudyChanged: () => void;
  /** Opens a step of this study, which is where each blocker's link goes. */
  onOpenStep?: (stepSlug: StudyStepSlug) => void;
  /**
   * What fills an open checkout — the payment form — given the credentials the last Pay or Try
   * again was answered with for the pending purchase, or null when none is held (a reload, or a
   * session still opening). Without it the checkout is said to be open and Cancel is the way out.
   */
  checkout?: (credentials: CheckoutCredentials | null, form: CheckoutFormActions) => ReactNode;
}

/**
 * The two numbers behind a refusal to field a study a simulated run could not answer: what the
 * audience asks for, and the ceiling the generator has. Both come off the refusal rather than from
 * a constant on this side, because a limit stated twice is a limit that gets raised once.
 */
const simulationCapacitySchema = z.object({
  target_completes: z.number().int().positive(),
  maximum: z.number().int().positive(),
});

/** How often the age beside the price is redrawn. Short enough that "just now" stops being a lie. */
const AGE_TICK_MS = 30_000;

/**
 * How often the study is re-read while its run is in flight. Eight sequential Remesh calls take
 * tens of seconds, and there is no event to wait on — the run happens in the worker.
 */
const LAUNCH_POLL_MS = 3_000;

/**
 * How long to keep asking before saying so.
 *
 * The bound is for the stops the server cannot name: a job nothing ever picked up, or a worker
 * that died between the step throwing and the reason being written. Those leave the study paid
 * and `ready` for as long as the tab is open, with nothing to tell them from a run still in
 * progress, so polling without a bound would be a page claiming progress indefinitely. A run
 * whose stop *was* recorded is answered by the payment's `fulfillment` reading `stopped` instead,
 * within one poll and with a way out attached.
 */
const LAUNCH_POLL_LIMIT = 100;

/**
 * How often a study whose checkout is open is re-read, and for how long: as long as a checkout
 * session stays open. A payment made in another tab, or confirmed by the provider while this one
 * waits, reaches the page on the next read.
 */
const CHECKOUT_POLL_MS = 5_000;
const CHECKOUT_POLL_LIMIT = CHECKOUT_SESSION_LIFETIME_MS / CHECKOUT_POLL_MS;

/**
 * How long after a checkout closed unpaid Launch still says so above the price: a day, the
 * default window a price holds for, so the line is about the price a researcher just saw.
 */
export const CLOSED_CHECKOUT_RECENT_MS = 24 * 60 * 60_000;

/**
 * How often a pending purchase this tab has its own word on — the form said it was paid, or the
 * test checkout expired it — is asked about, and for how long: two seconds after each answer, as
 * often as the server reads the provider for one purchase, thirty times. One still pending after
 * that is left to the open checkout's own watch, which the webhook or the sweep settles it under.
 */
const CONFIRM_POLL_MS = 2_000;
const CONFIRM_POLL_LIMIT = 60_000 / CONFIRM_POLL_MS;

/**
 * Whether the page was opened by the payment provider's return after the payment, and if so takes
 * that off the address so a reload does not count it again. The return carries nothing else this
 * side reads: the session is the server's to know, never the browser's to name.
 */
const takeCheckoutReturn = (): boolean => {
  const query = new URLSearchParams(window.location.search);
  if (query.get('checkout') !== 'returned') {
    return false;
  }

  query.delete('checkout');
  const rest = query.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${rest === '' ? '' : `?${rest}`}${window.location.hash}`,
  );

  return true;
};

/**
 * The refusals of a checkout request that say where the purchase now stands rather than what is
 * wrong with the study: still opening or briefly failing, the provider out of reach, the checkout
 * refused and released, opened through another provider, its session gone and released once it
 * expires, or already closed. Each is said as the server words it, and the study is read again;
 * only the first two are offered Try again.
 */
const CHECKOUT_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'checkout_opening',
  'payment_provider_unavailable',
  'checkout_failed',
  'checkout_other_provider',
  'checkout_session_missing',
  'checkout_closed',
]);

/**
 * How old the price is, in words.
 *
 * Shown because a researcher can judge an age for themselves. It never says "expired": a price
 * whose window has run out is replaced on screen by a new one rather than shown as lapsed, and the
 * window is said beside it as the time it holds until.
 */
export const quoteAgeLabel = (quote: Quote, now: Date): string => {
  const minutes = Math.floor((now.getTime() - new Date(quote.computed_at).getTime()) / 60_000);
  if (minutes < 1) {
    return 'Worked out just now.';
  }

  if (minutes === 1) {
    return 'Worked out a minute ago.';
  }

  if (minutes < 60) {
    return `Worked out ${minutes} minutes ago.`;
  }

  const hours = Math.floor(minutes / 60);

  return hours === 1 ? 'Worked out an hour ago.' : `Worked out ${hours} hours ago.`;
};

/** A price refused for good (`quote_refused`): why, and for an N out of range the most a study may ask for. */
interface QuoteRefusal {
  reason: QuoteRefusalReason;
  mostPeople: number | undefined;
}

const quoteRefusalReasonSchema = z.enum(QUOTE_REFUSAL_REASONS);
const mostPeopleSchema = z.int().positive();

/**
 * A refusal's detail, each part read on its own: a malformed maximum costs the refusal only its
 * number, never its reason, and a reason this bundle does not know reads as the general refusal.
 */
const readQuoteRefusal = (detail: unknown): QuoteRefusal => {
  const fields: Record<string, unknown> =
    typeof detail === 'object' && detail !== null ? { ...detail } : {};
  const reason = quoteRefusalReasonSchema.safeParse(fields.reason);
  const most = mostPeopleSchema.safeParse(fields.max_target_completes);

  return {
    reason: reason.success ? reason.data : 'remesh_refused',
    mostPeople: most.success ? most.data : undefined,
  };
};

/**
 * Where the price stands: being asked for, could not be had just now, refused for good, shown,
 * or past the end of its window and being asked for again. Only `shown` renders an amount, so no
 * state shows a number nobody was quoted or one that no longer holds.
 */
type PriceState =
  | { status: 'loading' }
  | { status: 'rechecking' }
  | { status: 'failed' }
  | { status: 'refused'; refusal: QuoteRefusal }
  | { status: 'shown'; view: QuoteView };

/** One object each, so a price in either state is the same value from render to render. */
const PRICE_LOADING_STATE: PriceState = { status: 'loading' };
const PRICE_RECHECKING_STATE: PriceState = { status: 'rechecking' };

/** The longest delay a timer keeps; a longer one fires at once, so a delay is capped and re-armed. */
const MAX_TIMER_MS = 2 ** 31 - 1;

/**
 * How long to wait before asking again for a price past its window, when the last ask answered the
 * same window (this clock ahead of the server's): soon, then less often, then once a minute.
 */
const RECHECK_BACKOFF_MS = [5_000, 15_000, 60_000] as const;

/**
 * What the study costs and the one action that spends it.
 *
 * The price is a single all-in number and is never broken down: what a researcher agrees to is
 * the total, and an itemisation invites a negotiation with the arithmetic rather than a decision
 * about the study. It is always the server's `total_cents`; nothing here works a price out.
 *
 * A live price lapses at its `expires_at`, and the panel asks for a new one there itself, showing
 * it — with the one it replaced, when the amount moved — before Pay is offered against it. A
 * payment refused because the price lapsed or the study moved under it is answered the same way.
 * A price a purchase already holds the study at is shown as accepted, and never lapses.
 *
 * Nothing here decides whether the study may launch. What stands in the way is the server's blocker
 * list, read from the predicates the purchase refuses on, and the panel renders it rather than
 * re-deriving it from the sidebar's rows, which would be a second opinion able to disagree with
 * the one the server refuses on. Pay is withheld while the list holds anything, and while it is
 * unknown.
 */
export const LaunchPanel = ({
  studyId,
  study,
  refreshToken,
  onStudyChanged,
  onOpenStep,
  checkout,
}: LaunchPanelProps) => {
  /** Kept with the study it was asked for, so a panel moved to another study shows none of its price. */
  const [priceRead, setPriceRead] = useState<{ studyId: string; price: PriceState } | null>(null);
  const price: PriceState = priceRead?.studyId === studyId ? priceRead.price : PRICE_LOADING_STATE;
  /** Bumped to ask for the price again with the one on screen taken down first. */
  const [reissueToken, setReissueToken] = useState(0);
  /**
   * The amount shown before the one on screen, when the two differ, so a price that moved while the
   * screen was open says so before anybody pays it. Cleared when a different quote at the same
   * amount replaces it.
   */
  const [changedFrom, setChangedFrom] = useState<string | null>(null);
  /** The last live price shown, for the study it was shown for. Survives a failed ask in between. */
  const lastShownRef = useRef<{ studyId: string; quoteId: string; total: string } | null>(null);
  /** Whether an ask for the price is out, so the one on screen is not offered while it may change. */
  const [asking, setAsking] = useState(false);
  /** How many times the price past its window has been asked for again, by that quote and window. */
  const [recheckAttempt, setRecheckAttempt] = useState<{ key: string; attempt: number } | null>(
    null,
  );
  /**
   * Whether the quote's N has already been checked against the audience's once, for the quote on
   * screen. A disagreement is a race between two reads, so both are read again once; one that
   * survives that is an alert, not a relabelled N.
   */
  const reconciledRef = useRef<string | null>(null);
  /** The price block, which takes focus when the control the researcher was on goes away. */
  const priceBlockRef = useRef<HTMLDivElement>(null);
  const payRef = useRef<HTMLButtonElement>(null);
  /** Set when the next render should move focus to the price block. */
  const focusPriceBlockRef = useRef(false);
  /** The payment's region, which takes focus when Pay or Cancel goes away under the researcher. */
  const paymentRegionRef = useRef<HTMLDivElement>(null);
  /** Set when the next render should move focus to the payment's region. */
  const focusPaymentRef = useRef(false);
  const [notice, setNotice] = useState<string | null>(null);
  /** The payment request out, if any. Every payment control waits for it. */
  const [action, setAction] = useState<PaymentAction | null>(null);
  const busy = action !== null;
  /** The last checkout request the server refused, said until another request is made. */
  const [refusal, setRefusal] = useState<CheckoutRefusal | null>(null);
  /**
   * The payment status one of this panel's requests was answered with, for the study it was asked
   * about and stamped with when the answer arrived.
   *
   * It stands in for the study's own `payment` until a read **asked for after the answer** reaches
   * the panel (`Study.readAt`), which carries the same status or a newer one. A read already out
   * when the request was sent, or the same read handed down again with a local change such as a
   * rename, is older than the answer and does not replace it — so a price and Pay are never offered
   * again over a purchase that holds the study, and a restarted run's stop is not shown again under
   * a read from before the restart, which is what keeps a second press from sending a second run.
   */
  const [answered, setAnswered] = useState<{
    studyId: string;
    at: number;
    /** When the request it answers was sent, which orders two answers that crossed. */
    askedAt: number;
    payment: StudyPayment;
  } | null>(null);
  /** The answer standing, for a request finishing after it to tell whether it is the newer. */
  const answeredRef = useRef(answered);
  useEffect(() => {
    answeredRef.current = answered;
  });
  /** The study the panel is on now, for an answer arriving after it moved to another. */
  const studyIdRef = useRef(studyId);
  useEffect(() => {
    studyIdRef.current = studyId;
  });
  /**
   * The embedded checkout's credentials, as the last Pay or Try again was answered with them, for
   * the purchase they open. Never stored anywhere but here, and given to `checkout` only while that
   * purchase is the pending one.
   */
  const [credentials, setCredentials] = useState<{
    purchaseId: string;
    checkout: CheckoutCredentials;
  } | null>(null);
  const payment: StudyPayment | null =
    answered !== null &&
    answered.studyId === studyId &&
    (study?.readAt ?? Number.NEGATIVE_INFINITY) < answered.at
      ? answered.payment
      : (study?.payment ?? null);
  /**
   * The pending purchase this tab has its own word on, while the server settles it: the payment
   * form said it was paid (`confirming`), or the test checkout expired it (`expiring`).
   */
  const [settling, setSettling] = useState<{
    studyId: string;
    purchaseId: string;
    kind: 'confirming' | 'expiring';
  } | null>(null);
  /** The purchase whose quick checks ran out with it still pending. */
  const [slowPurchase, setSlowPurchase] = useState<string | null>(null);
  /**
   * The pending purchase whose state was asked of the provider when the panel arrived on it, so
   * Continue is never offered for a session the provider already reports paid.
   */
  const [arrivalChecked, setArrivalChecked] = useState<string | null>(null);
  /** Whether the provider's return is being settled, which answers the arrival's question too. */
  const [returning, setReturning] = useState(false);
  /** The same, as the effects of the render that starts it see it. */
  const returningRef = useRef(false);
  const [now, setNow] = useState(() => new Date());
  /** Bumped when a payment is refused for a reason the blocker list explains, to read it again. */
  const [recheck, setRecheck] = useState(0);

  const state = study?.state ?? null;
  /**
   * Past the point of paying: Launch says when the study launched and keeps its payment problems,
   * and stops talking about a price.
   */
  const launched = state !== null && isLaunchedState(state);
  /**
   * Paid for and not yet fielding. Narrowed to `ready` deliberately: a study only sits between the
   * payment and the launch while it is still ready, and everything past that has moved on. Read as
   * "not live" instead, a finished study reports itself as still being set up.
   */
  const settingUp =
    !launched &&
    state === 'ready' &&
    (payment?.kind === 'paid' || study?.paidAt != null || study?.lockedAt != null);
  /** A purchase holds the study while its checkout is open: Launch shows the checkout, not a price. */
  const checkoutPending = !launched && !settingUp && payment?.kind === 'pending';
  const pendingPurchaseId = payment?.kind === 'pending' ? payment.purchase.id : null;
  /** The settling of the purchase on screen, while it is still pending. */
  const settlingNow =
    pendingPurchaseId !== null &&
    settling?.studyId === studyId &&
    settling.purchaseId === pendingPurchaseId
      ? settling
      : null;
  /** Whether the purchase this tab saw paid in the form is still pending: Launch says it is being confirmed. */
  const confirming = settlingNow?.kind === 'confirming';
  const settlingActive = settlingNow !== null;
  /** Whether the quick checks on it have run out. */
  const settlingSlow = settlingActive && slowPurchase === pendingPurchaseId;
  /** Which of Launch's views is on screen. A refusal is about the view it was said on. */
  const shownView = launched
    ? 'launched'
    : settingUp
      ? 'setting-up'
      : checkoutPending
        ? 'checkout'
        : 'priced';
  const lastView = useRef(shownView);
  useEffect(() => {
    // A refusal said on one view is not about the next: a cancel refused because the checkout had
    // closed meanwhile must not stay up over the price that read then shows.
    if (lastView.current !== shownView) {
      lastView.current = shownView;
      setRefusal(null);
    }
  }, [shownView]);
  const launchCheck = useLaunchCheck({
    studyId,
    active: !launched && !settingUp && !checkoutPending && (state === 'ready' || state === 'draft'),
    ready: state === 'ready',
    refreshToken,
    recheckToken: recheck,
  });
  const blocked =
    launchCheck.failed || launchCheck.blockers === null || launchCheck.blockers.length > 0;

  /** Only a study that can still be paid for has a price, and a summary of what it buys. */
  const priced = state === 'ready' && !settingUp && !checkoutPending;
  const summary = useLaunchSummaryReads({ studyId, active: priced, refreshToken });
  const { reread } = summary;
  /**
   * Read through a ref so the price is not asked for again whenever the shell re-renders, which
   * may give this callback a new identity on any render.
   */
  const onStudyChangedRef = useRef(onStudyChanged);
  useEffect(() => {
    onStudyChangedRef.current = onStudyChanged;
  });

  /** Takes the price down and asks for it again: a refused payment, a mismatch, a retry. */
  const reissue = useCallback(() => {
    setPriceRead({ studyId, price: PRICE_LOADING_STATE });
    setReissueToken((token) => token + 1);
  }, [studyId]);

  /** Asks for the price again and leaves what is on screen, which decides for itself how it shows. */
  const askAgain = useCallback(() => setReissueToken((token) => token + 1), []);

  useEffect(() => {
    if (lastShownRef.current !== null && lastShownRef.current.studyId !== studyId) {
      lastShownRef.current = null;
      reconciledRef.current = null;
      setChangedFrom(null);
    }
  }, [studyId]);

  useEffect(() => {
    // Only a study that can still be paid for has a price worth asking about. A paid one is
    // waiting on its run, and a request for a number nobody will be shown is a request the panel
    // then has to have an opinion about when it fails.
    if (!priced) {
      setPriceRead(null);
      setAsking(false);

      return;
    }

    // A re-ask after a turn leaves the price on screen with Pay withheld until it answers: the
    // server answers the same quote inside its window, so taking it down would only flash. Every
    // other re-ask has already taken it down through `reissue`, or shows it as past its window.
    let current = true;
    setAsking(true);
    issueQuote(studyId).then(
      (view) => {
        if (current) {
          setAsking(false);
          setPriceRead({ studyId, price: { status: 'shown', view } });
        }
      },
      (failure: unknown) => {
        if (!current) {
          return;
        }

        setAsking(false);
        // The price failing to load is not a study with no price, and it must not read as one:
        // the button is withheld rather than offered against a number nobody has, and the stored
        // price goes with it — a stale one left on screen under the failure is the number most
        // likely to be wrong. A refusal no wait changes is said as one, with no "Try again"; an
        // outage, a throttle or a network error is the failure a retry can clear.
        const code = domainCodeOf(failure);
        setPriceRead({
          studyId,
          price:
            code === 'quote_refused' && isApiError(failure)
              ? { status: 'refused', refusal: readQuoteRefusal(failure.detail) }
              : { status: 'failed' },
        });
        if (code === 'study_not_ready') {
          // The study left ready under the panel; the shell's read is what takes the price away.
          onStudyChangedRef.current();
        }
      },
    );

    return () => {
      current = false;
    };
  }, [studyId, priced, refreshToken, reissueToken]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), AGE_TICK_MS);

    return () => clearInterval(timer);
  }, []);

  const shown = price.status === 'shown' ? price.view : null;
  /** A live price, as opposed to one a purchase holds; only this kind lapses. */
  const liveQuote = shown !== null && !shown.accepted ? shown.quote : null;
  /**
   * Past its window on this page's clock, whatever the server answered last. Such a price is never
   * offered: it is taken down and asked for again until one with a later window arrives.
   */
  const lapsed = liveQuote !== null && !isQuoteLive(liveQuote, now);

  useEffect(() => {
    // At the moment the window ends rather than at the next age tick, so Pay is never offered
    // against a price for up to half a minute after it stopped holding. A window longer than a
    // timer can hold is waited out in pieces, so it never ends early.
    if (liveQuote === null || lapsed) {
      return;
    }

    const timer = setTimeout(
      () => setNow(new Date()),
      Math.min(MAX_TIMER_MS, Math.max(0, Date.parse(liveQuote.expires_at) - Date.now())),
    );

    return () => clearTimeout(timer);
  }, [liveQuote, lapsed, now]);

  const lapsedKey =
    lapsed && liveQuote !== null ? `${liveQuote.quote_id}:${liveQuote.expires_at}` : null;

  useEffect(() => {
    // Asked again at once when the window ends, and then on a backoff for as long as the server
    // answers the same window — which it does when this clock runs ahead of the server's.
    if (lapsedKey === null) {
      return;
    }

    const attempt = recheckAttempt?.key === lapsedKey ? recheckAttempt.attempt : 0;
    const ask = () => {
      setRecheckAttempt({ key: lapsedKey, attempt: attempt + 1 });
      askAgain();
    };

    if (attempt === 0) {
      if (payRef.current !== null && document.activeElement === payRef.current) {
        focusPriceBlockRef.current = true;
      }

      ask();

      return;
    }

    const timer = setTimeout(
      ask,
      RECHECK_BACKOFF_MS[Math.min(attempt, RECHECK_BACKOFF_MS.length) - 1],
    );

    return () => clearTimeout(timer);
  }, [lapsedKey, recheckAttempt, askAgain]);

  const audienceRead = summary.reads.audience;
  /**
   * The quote's N against the audience's: they are the same number by construction, so a
   * disagreement is two reads that crossed, and the price is not shown beside a count it was not
   * worked out for.
   */
  const mismatch =
    shown !== null &&
    audienceRead.status === 'read' &&
    audienceRead.value.completes !== shown.quote.target_completes;
  const quoteKey =
    shown === null ? null : `${shown.quote.quote_id ?? ''}:${shown.quote.computed_at}`;
  const reconcileOnce = mismatch && reconciledRef.current !== quoteKey;

  useEffect(() => {
    if (reconcileOnce && quoteKey !== null) {
      reconciledRef.current = quoteKey;
      reread();
      reissue();
    }
  }, [reconcileOnce, quoteKey, reread, reissue]);

  /** The price is shown only inside its window, and once the audience it is for agrees with it. */
  const priceView: PriceState = useMemo(
    () =>
      price.status !== 'shown'
        ? price
        : lapsed
          ? PRICE_RECHECKING_STATE
          : audienceRead.status === 'pending' || reconcileOnce
            ? PRICE_LOADING_STATE
            : audienceRead.status === 'failed' || mismatch
              ? { status: 'failed' }
              : price,
    [price, lapsed, audienceRead.status, reconcileOnce, mismatch],
  );

  /**
   * A price on screen that may be about to change: a re-ask after a turn, or the summary's reads,
   * still out. It stays shown, and Pay waits for both to answer.
   */
  const updating = priceView.status === 'shown' && (asking || summary.refreshing);

  /** A live price as it is rendered and offered: the only one "the price changed" may name. */
  const renderedLive =
    priceView.status === 'shown' && !priceView.view.accepted && !updating
      ? priceView.view.quote
      : null;

  useLayoutEffect(() => {
    if (renderedLive === null) {
      return;
    }

    const total = priceLabel(renderedLive);
    const last = lastShownRef.current;
    if (last !== null && last.studyId === studyId && last.total !== total) {
      setChangedFrom(last.total);
    } else if (last?.quoteId !== renderedLive.quote_id) {
      setChangedFrom(null);
    }

    lastShownRef.current = { studyId, quoteId: renderedLive.quote_id, total };
  }, [renderedLive, studyId]);

  /**
   * The study's latest checkout, when it closed without a payment within the last
   * `CLOSED_CHECKOUT_RECENT_MS` on the page's clock: "the price changed" names the price it held
   * when the new one differs, and the line above the price says it closed — except right after a
   * cancellation made here, whose own notice says so already. Past that window it is history,
   * and Launch says nothing of it.
   */
  const closedAttempt =
    payment?.kind === 'unpaid' &&
    payment.purchase !== null &&
    (payment.purchase.state === 'cancelled' || payment.purchase.state === 'expired') &&
    payment.purchase.closedAt !== null &&
    now.getTime() - Date.parse(payment.purchase.closedAt) < CLOSED_CHECKOUT_RECENT_MS
      ? { state: payment.purchase.state, total: priceLabel(payment.purchase.quote) }
      : null;
  const shownChangedFrom =
    changedFrom ??
    (closedAttempt !== null &&
    renderedLive !== null &&
    closedAttempt.total !== priceLabel(renderedLive)
      ? closedAttempt.total
      : null);

  useEffect(() => {
    if (focusPriceBlockRef.current && priceBlockRef.current !== null) {
      focusPriceBlockRef.current = false;
      priceBlockRef.current.focus();
    }

    if (focusPaymentRef.current && paymentRegionRef.current !== null) {
      focusPaymentRef.current = false;
      paymentRegionRef.current.focus();
    }
  });

  const retryPrice = useCallback(() => {
    // The button goes with the failure it answers, so focus goes to where the price will be.
    focusPriceBlockRef.current = true;
    reconciledRef.current = null;
    reread();
    reissue();
  }, [reread, reissue]);

  const waitedTooLong = useBoundedPoll({
    active: settingUp,
    intervalMs: LAUNCH_POLL_MS,
    limit: LAUNCH_POLL_LIMIT,
    onTick: onStudyChanged,
  });

  /** An open checkout, watched for the payment or the closing the server records without the page. */
  const watchStopped = useBoundedPoll({
    active: checkoutPending,
    intervalMs: CHECKOUT_POLL_MS,
    limit: CHECKOUT_POLL_LIMIT,
    onTick: onStudyChanged,
  });

  /**
   * Every request Launch makes about a payment — paying at the price on screen, asking for the
   * checkout again, cancelling it, and starting a paid study's stopped setup again — and everything
   * the panel says about how it went. The caller makes the request and answers the payment status
   * it got back, with what to say about it.
   *
   * The sentence for a failure nothing else recognises travels with the request: a cancellation and
   * a setup retry spend nothing, and a refusal they cannot name must not tell a researcher a payment
   * went wrong.
   *
   * Answers whether the call landed, which is what the restart needs in order to know it has a run
   * in flight to wait for.
   */
  const run = useCallback(
    async ({
      kind,
      send,
      unrecognisedRefusal,
    }: {
      kind: PaymentAction;
      /**
       * Makes the call, and answers the payment status, what to say about it and, for a call that
       * opens the checkout, its credentials.
       */
      send: () => Promise<{
        payment: StudyPayment;
        notice: string | null;
        checkout?: CheckoutCredentials | null;
      }>;
      unrecognisedRefusal: string;
    }) => {
      setAction(kind);
      setNotice(null);
      setRefusal(null);
      const askedAt = Date.now();
      try {
        const outcome = await send();
        setAnswered({ studyId, at: Date.now(), askedAt, payment: outcome.payment });
        if (outcome.checkout !== undefined) {
          setCredentials(
            outcome.checkout !== null && outcome.payment.kind === 'pending'
              ? { purchaseId: outcome.payment.purchase.id, checkout: outcome.checkout }
              : null,
          );
        }
        setNotice(outcome.notice);
        // The control that was pressed has gone with the view it was on: to the price once nothing
        // holds the study, to the payment while a purchase does.
        if (outcome.payment.kind === 'unpaid') {
          focusPriceBlockRef.current = true;
        } else {
          focusPaymentRef.current = true;
        }
        onStudyChanged();

        return true;
      } catch (failure) {
        const code = domainCodeOf(failure);

        // The list says why, with the step: read it again rather than leave it describing the
        // study as it was before the refusal.
        if (code !== null && LAUNCH_BLOCKER_CODES.has(code)) {
          setRecheck((count) => count + 1);
        }

        if (code === 'quote_stale' || code === 'quote_expired') {
          // The price on screen no longer stands, and the refusal carries none to replace it: the
          // number to agree to is the one a new quote states, so the panel asks for it and shows
          // it, with the old one beside it when the amount moved, and Pay waits for another press.
          // Focus goes to the price, since the button the researcher pressed is withheld now.
          focusPriceBlockRef.current = true;
          reissue();
        } else if (isApiError(failure) && CHECKOUT_REFUSAL_CODES.has(failure.code)) {
          // About where the purchase stands rather than about the study: said in the server's
          // words, which are written for the researcher, and the study read again so the panel
          // shows the purchase as it now is — still pending, released, or closed.
          setRefusal({ code: failure.code, message: failure.message });
          onStudyChanged();
        } else if (code === 'provisioning_already_running') {
          // The server refuses a second run at a study that already has one, and this is the
          // sentence for the only way a researcher reaches that refusal: another tab, or another
          // person, pressed the same button first. Nothing went wrong, and nothing was charged.
          setNotice('This study is already being set up. Nothing further is needed here.');
        } else if (code === 'study_too_large_to_simulate') {
          // The one refusal a researcher can act on themselves, so it is the one refusal said in the
          // product's words rather than the server's. The server sentence is written for the log and
          // for the agent — lowercase, "completes", a dash — and it names no place to go; this one
          // names the card the number lives on.
          //
          // Parsed rather than cast, like every other response this client reads: a moved detail
          // shape must degrade to a sentence that is still true rather than render "at most
          // undefined".
          const capacity = isApiError(failure)
            ? simulationCapacitySchema.safeParse(failure.detail)
            : undefined;

          setNotice(
            capacity?.success === true
              ? `This study asks for ${String(capacity.data.target_completes)} people, and this environment can run at most ${String(capacity.data.maximum)}. Lower the sample size in the Audience card and try again.`
              : 'This study asks for more people than this environment can run. Lower the sample size in the Audience card and try again.',
          );
        } else {
          setNotice(failure instanceof Error ? failure.message : unrecognisedRefusal);
        }

        return false;
      } finally {
        setAction(null);
      }
    },
    [onStudyChanged, reissue, studyId],
  );

  const pay = useCallback(async () => {
    if (priceView.status !== 'shown') {
      return;
    }

    const { view } = priceView;
    // A window that ran out while this tab's timers were held back is not sent: the clock is read
    // again, which takes the price down and asks for a new one.
    if (!view.accepted && !isQuoteLive(view.quote, new Date())) {
      setNow(new Date());

      return;
    }

    // A price with no id is not an issued quote and cannot be bought: it is asked for again.
    const quoteId = view.quote.quote_id;
    if (quoteId === undefined) {
      reissue();

      return;
    }

    await run({
      kind: 'pay',
      // A pending answer says nothing here: the checkout it opened is what the panel shows next.
      send: async () => {
        const { status, checkout: opened } = await createPurchase(studyId, quoteId);

        return {
          payment: status,
          notice: status.kind === 'paid' ? PAYMENT_TAKEN : null,
          checkout: opened,
        };
      },
      unrecognisedRefusal: 'The payment could not be completed.',
    });
  }, [priceView, reissue, run, studyId]);

  /** Asks for the pending purchase's checkout again: a session not opened yet, or out of reach. */
  const reopen = useCallback(async () => {
    await run({
      kind: 'open',
      send: async () => {
        const { status, checkout: opened } = await openCheckout(studyId);

        return { payment: status, notice: null, checkout: opened };
      },
      unrecognisedRefusal: 'The checkout could not be opened just now.',
    });
  }, [run, studyId]);

  /**
   * Cancels the pending checkout. The server closes the session before it releases anything, so a
   * payment that went through first stands, and the study goes on to be set up.
   */
  const cancel = useCallback(async () => {
    await run({
      kind: 'cancel',
      send: async () => {
        const closed = await cancelCheckout(studyId);

        return {
          payment: closed,
          notice:
            closed.kind === 'paid'
              ? PAID_BEFORE_CANCEL
              : closed.kind === 'unpaid'
                ? CHECKOUT_CANCELLED
                : null,
        };
      },
      unrecognisedRefusal: 'The checkout could not be cancelled just now.',
    });
  }, [run, studyId]);

  /**
   * Starting a stopped run over, through the setup retry: it takes no payment and asks for no
   * price, and the server refuses it for a study whose run is still under way.
   *
   * The answer says the run is queued again, and it stands until a read asked for after it arrives
   * (`answered`), so the button does not come back under a read that was already out. Two runs
   * would both make the step's outbound call before either wrote, and the conditional write that
   * catches the loser catches it too late.
   */
  const retry = useCallback(async () => {
    await run({
      kind: 'retry',
      // A study paid for already agrees to no price: its restart asks for none, so a panel that
      // cannot be read never holds it up and no quote is issued for it.
      send: async () => ({ payment: await retrySetup(studyId), notice: SETUP_RESTARTED }),
      unrecognisedRefusal: 'The restart could not be completed.',
    });
  }, [run, studyId]);

  /**
   * Asks the server to settle the purchase from what the provider reports now, and shows what it
   * answered until a later read of the study. It sends nothing about the payment: the provider's
   * report is the only confirmation. A failure answers nothing, and the next check or read asks
   * again. An answer for a study the panel has left, or one older than the answer standing, is
   * dropped and answers nothing.
   */
  const settle = useCallback(async (): Promise<StudyPayment | null> => {
    const askedAt = Date.now();
    let status: StudyPayment;
    try {
      status = await refreshPurchase(studyId);
    } catch {
      return null;
    }

    const standing = answeredRef.current;
    if (
      studyIdRef.current !== studyId ||
      (standing !== null && standing.studyId === studyId && standing.askedAt > askedAt)
    ) {
      return null;
    }

    setAnswered({ studyId, at: Date.now(), askedAt, payment: status });
    if (status.kind === 'paid') {
      setNotice(PAYMENT_TAKEN);
    }

    if (status.kind !== 'pending') {
      onStudyChangedRef.current();
    }

    return status;
  }, [studyId]);
  const settleRef = useRef(settle);
  useEffect(() => {
    settleRef.current = settle;
  });

  /**
   * The payment form says the payment is complete: Launch says it is being confirmed, in place of
   * the form and Cancel, and asks the server, which reads the provider.
   */
  const completed = useCallback(() => {
    if (pendingPurchaseId !== null) {
      setSettling({ studyId, purchaseId: pendingPurchaseId, kind: 'confirming' });
      focusPaymentRef.current = true;
    }
  }, [pendingPurchaseId, studyId]);

  /** The checkout closed under the form: the status it is in now is shown, and the study read again. */
  const checkoutClosed = useCallback(
    (status: StudyPayment) => {
      const closedAt = Date.now();
      setAnswered({ studyId, at: closedAt, askedAt: closedAt, payment: status });
      onStudyChangedRef.current();
    },
    [studyId],
  );

  /**
   * The server refused the checkout for good when the form asked for it: said in its words, as a
   * refusal of Pay or Try again is, and the study read again.
   */
  const checkoutRefused = useCallback((refused: CheckoutRefusal) => {
    setRefusal(refused);
    onStudyChangedRef.current();
  }, []);

  /**
   * The fake's checkout was expired from its test box: watched like a confirmation until the
   * server closes it, and focus goes to the price once it has.
   */
  const expired = useCallback(() => {
    if (pendingPurchaseId !== null) {
      setSettling({ studyId, purchaseId: pendingPurchaseId, kind: 'expiring' });
    }
  }, [pendingPurchaseId, studyId]);

  /** One more ask, once the quick checks have run out. */
  const checkAgain = useCallback(async () => {
    setAction('refresh');
    try {
      await settle();
    } finally {
      setAction(null);
    }
  }, [settle]);

  /** What the purchase being settled is waiting for, read by its checks without restarting them. */
  const settlingKindRef = useRef(settlingNow?.kind ?? null);
  useEffect(() => {
    settlingKindRef.current = settlingNow?.kind ?? null;
  });

  useEffect(() => {
    // Asked at once, then two seconds after each answer and never while one is out, through the
    // one function every other check uses; the server throttles its provider reads to the same
    // interval.
    if (!settlingActive || pendingPurchaseId === null) {
      return;
    }

    const purchaseId = pendingPurchaseId;
    let checks = 0;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ask = () => {
      checks += 1;
      void settleRef.current().then((status) => {
        if (stopped) {
          return;
        }

        if (status?.kind === 'unpaid' && settlingKindRef.current === 'expiring') {
          // The test checkout's expiry closed it: focus goes to the price that replaces it.
          focusPriceBlockRef.current = true;
        }

        if (checks >= CONFIRM_POLL_LIMIT) {
          setSlowPurchase(purchaseId);
        } else {
          timer = setTimeout(ask, CONFIRM_POLL_MS);
        }
      });
    };
    ask();

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [settlingActive, pendingPurchaseId]);

  useEffect(() => {
    // The provider's return after a payment that needed to leave the page: it is settled like the
    // form's own completion, and the address is cleaned before anything is asked.
    if (!takeCheckoutReturn()) {
      return;
    }

    returningRef.current = true;
    setReturning(true);
    void settleRef.current().then((status) => {
      returningRef.current = false;
      setReturning(false);
      if (status?.kind === 'pending') {
        setSettling({ studyId, purchaseId: status.purchase.id, kind: 'confirming' });
      }
    });
  }, [studyId]);

  /** Whether the panel has a payment form to offer, which is when Continue is asked about. */
  const offersForm = checkout !== undefined;
  const arrivalPending =
    offersForm &&
    payment?.kind === 'pending' &&
    payment.checkout === 'open' &&
    credentials?.purchaseId !== payment.purchase.id &&
    !settlingActive &&
    !returning &&
    arrivalChecked !== payment.purchase.id;

  useEffect(() => {
    // An open checkout this panel holds no credentials for may have been paid in another tab or
    // before a reload: the provider is asked once before Continue is offered on it.
    if (!arrivalPending || pendingPurchaseId === null || returningRef.current) {
      return;
    }

    const purchaseId = pendingPurchaseId;
    void settleRef.current().finally(() => setArrivalChecked(purchaseId));
  }, [arrivalPending, pendingPurchaseId]);

  if (study === null) {
    return null;
  }

  /** The study's open payment problems, said on every view whatever else is. */
  const problems = <PaymentProblems problems={payment?.problems ?? []} />;

  if (launched) {
    // Payment content and when it launched, and nothing about progress: the Fielding step after
    // this one shows how the study is going, and watches it. A finished study offers its Brief.
    return (
      <section className="launch-panel" data-test-id="launch-panel">
        {problems}

        <p data-test-id="launch-launched-summary">
          {study.launchedAt === null
            ? LAUNCHED_UNDATED
            : launchedSummaryLine(
                dateOf(study.launchedAt),
                payment?.kind === 'paid' || study.paidAt !== null,
              )}
        </p>

        {/* One click to the finished study's Brief, which is what the researcher came back for. */}
        {state === 'complete' && onOpenStep !== undefined && (
          <button
            type="button"
            data-test-id="launch-open-brief"
            onClick={() => onOpenStep('brief')}
          >
            {OPEN_BRIEF_LABEL}
          </button>
        )}
      </section>
    );
  }

  if (settingUp || checkoutPending) {
    return (
      <section className="launch-panel" data-test-id="launch-panel">
        {problems}

        <LaunchPayment
          action={action}
          checkout={
            checkout === undefined
              ? undefined
              : checkout(
                  payment?.kind === 'pending' && credentials?.purchaseId === payment.purchase.id
                    ? credentials.checkout
                    : null,
                  {
                    onComplete: completed,
                    onClosed: checkoutClosed,
                    onExpired: expired,
                    onRefused: checkoutRefused,
                    onContinue: () => void reopen(),
                    continuing: action === 'open',
                    checking: arrivalPending,
                    busy,
                  },
                )
          }
          confirming={confirming}
          confirmingSlow={confirming && settlingSlow}
          onCheckAgain={() => void checkAgain()}
          notice={notice}
          onCancel={() => void cancel()}
          onOpenCheckout={() => void reopen()}
          onRetrySetup={() => void retry()}
          payment={payment}
          refusal={refusal}
          regionRef={paymentRegionRef}
          study={study}
          waitedTooLong={waitedTooLong}
          watchStopped={watchStopped}
        />
      </section>
    );
  }

  const blockerList = (
    <LaunchBlockers studyId={studyId} state={launchCheck} onOpenStep={onOpenStep} />
  );

  if (state !== 'ready') {
    return (
      <section className="launch-panel" data-test-id="launch-panel">
        {problems}

        <p data-test-id="launch-blocked">
          The price to launch this study will appear here once nothing below stands in the way.
        </p>

        {blockerList}
      </section>
    );
  }

  return (
    <section className="launch-panel" data-test-id="launch-panel">
      {problems}

      {closedAttempt !== null && notice !== CHECKOUT_CANCELLED && (
        <p data-test-id="launch-checkout-closed">{closedCheckoutLine(closedAttempt.state)}</p>
      )}

      {blockerList}

      <LaunchSummary study={study} reads={summary.reads} />

      <div
        aria-label="Price"
        className="launch-price-block"
        data-test-id="launch-price-block"
        ref={priceBlockRef}
        role="region"
        tabIndex={-1}
      >
        {/*
          One live region for the whole block, there from the start, whose words change: a region
          that mounts with its text already in it is not announced.
        */}
        <p className="launch-quote-status" data-test-id="launch-quote-status" role="status">
          {priceView.status === 'loading' ? (
            <span data-test-id="launch-quote-loading">{PRICE_LOADING}</span>
          ) : priceView.status === 'rechecking' ? (
            <span data-test-id="launch-quote-rechecking">{PRICE_RECHECKING}</span>
          ) : priceView.status === 'shown' && updating ? (
            <span data-test-id="launch-quote-updating">{PRICE_UPDATING}</span>
          ) : priceView.status === 'shown' &&
            !priceView.view.accepted &&
            shownChangedFrom !== null ? (
            <span className="launch-quote-changed" data-test-id="launch-quote-changed">
              {priceChangedLine(shownChangedFrom)}
            </span>
          ) : null}
        </p>

        {priceView.status === 'failed' && (
          <>
            <p className="chat-error" data-test-id="launch-quote-failed" role="alert">
              {PRICE_FAILED}
            </p>
            <button data-test-id="launch-quote-retry" onClick={retryPrice} type="button">
              {PRICE_RETRY}
            </button>
          </>
        )}

        {priceView.status === 'refused' && (
          <>
            <p className="chat-error" data-test-id="launch-quote-refused" role="alert">
              {QUOTE_REFUSAL_MESSAGES[priceView.refusal.reason]}
              {priceView.refusal.reason === 'target_completes_out_of_range' &&
                priceView.refusal.mostPeople !== undefined &&
                ` ${mostPeopleLine(priceView.refusal.mostPeople)}`}
            </p>
            {priceView.refusal.reason === 'target_completes_out_of_range' &&
              onOpenStep !== undefined && (
                <button
                  data-test-id="launch-quote-open-audience"
                  onClick={() => onOpenStep('audience')}
                  type="button"
                >
                  {OPEN_AUDIENCE_STEP}
                </button>
              )}
          </>
        )}

        {priceView.status === 'shown' && (
          <>
            <p className="launch-price" data-test-id="launch-price">
              {priceLabel(priceView.view.quote)}
            </p>
            <p className="launch-quote-detail" data-test-id="launch-quote-minutes">
              {pricedMinutesLine(displayMinutes(priceView.view.quote.estimated_duration_minutes))}
            </p>
            {priceView.view.accepted ? (
              <p className="launch-quote-detail" data-test-id="launch-quote-accepted">
                {priceAcceptedLine(priceTimeLabel(priceView.view.quote.computed_at))} {PRICE_ALL_IN}
              </p>
            ) : (
              <p className="launch-quote-detail" data-test-id="launch-quote-age">
                {quoteAgeLabel(priceView.view.quote, now)}{' '}
                {priceHoldsUntil(priceTimeLabel(priceView.view.quote.expires_at))} {PRICE_ALL_IN}
              </p>
            )}
          </>
        )}

        <p className="launch-quote-detail" data-test-id="launch-fielding">
          {FIELDING_TIME}
        </p>
      </div>

      {/*
        Always there on a ready study and enabled only against a price on screen that nothing is
        about to change, so the control does not move while a price is asked for again.
      */}
      <button
        className="button-primary"
        data-test-id="launch-pay"
        disabled={busy || blocked || priceView.status !== 'shown' || updating}
        onClick={() => void pay()}
        ref={payRef}
        type="button"
      >
        {action === 'pay' ? PAYING_LABEL : PAY_LABEL}
      </button>

      {refusal !== null && (
        <p className="chat-error" data-test-id="launch-payment-refused" role="alert">
          {refusal.message}
        </p>
      )}

      {notice !== null && <p data-test-id="launch-notice">{notice}</p>}
    </section>
  );
};
